import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Avatar,
  Button,
  Tooltip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import {
  Person,
  PersonAdd,
  Email,
  Phone,
  Business,
  Close,
} from '@mui/icons-material';
import { userAPI } from '../../services/api';
import { toast } from 'react-toastify';

/**
 * Reusable Salesperson Select component with autocomplete and "Add New" functionality
 * 
 * @param {string} value - The current salesperson ID
 * @param {function} onChange - Callback when selection changes, receives salesperson ID
 * @param {string} label - Optional label text (default: "Salesperson")
 * @param {object} sx - Optional additional styles for the container
 */
const SalespersonSelect = ({ value, onChange, label = 'Salesperson', sx = {} }) => {
  const [salespersons, setSalespersons] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newSalesperson, setNewSalesperson] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    role: 'sales',
  });

  useEffect(() => {
    fetchSalespersons();
  }, []);

  const fetchSalespersons = async () => {
    try {
      const response = await userAPI.getSalespersons();
      setSalespersons(response.data);
    } catch (error) {
      console.error('Error fetching salespersons:', error);
    }
  };

  const handleAddSalesperson = async () => {
    if (!newSalesperson.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (!newSalesperson.email.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    try {
      const response = await userAPI.create({
        name: newSalesperson.name,
        email: newSalesperson.email,
        phone: newSalesperson.phone || '',
        department: newSalesperson.department || '',
        role: newSalesperson.role || 'sales',
      });
      
      const createdSalesperson = response.data.user;
      setSalespersons([...salespersons, createdSalesperson]);
      onChange(createdSalesperson._id);
      
      setNewSalesperson({
        name: '',
        email: '',
        phone: '',
        department: '',
        role: 'sales',
      });
      setDialogOpen(false);
      toast.success('Salesperson created successfully');
    } catch (error) {
      console.error('Error creating salesperson:', error);
      toast.error(error.response?.data?.message || 'Failed to create salesperson');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setNewSalesperson({
      name: '',
      email: '',
      phone: '',
      department: '',
      role: 'sales',
    });
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, ...sx }}>
        {label && (
          <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
            {label} <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
          </Typography>
        )}
        <Autocomplete
          size="small"
          sx={{ flex: 1 }}
          options={salespersons}
          getOptionLabel={(option) => option?.name || ''}
          value={salespersons.find(s => s._id === value) || null}
          onChange={(event, newValue) => {
            onChange(newValue?._id || '');
          }}
          renderOption={(props, option) => (
            <li {...props} key={option._id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 24, height: 24, fontSize: '12px', bgcolor: '#00a09d' }}>
                  {option.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: '13px' }}>{option.name}</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#666' }}>{option.email}</Typography>
                </Box>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select..."
              variant="standard"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                sx: { fontSize: '13px' },
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    <InputAdornment position="end">
                      <Tooltip title="Add new salesperson">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            setDialogOpen(true);
                          }}
                          sx={{ p: 0.5, color: '#00a09d' }}
                        >
                          <PersonAdd sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  </>
                ),
              }}
              sx={{ borderBottom: '1px solid #dee2e6' }}
            />
          )}
          noOptionsText={
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Typography sx={{ fontSize: '13px', color: '#666', mb: 1 }}>No salespersons found</Typography>
              <Button
                size="small"
                startIcon={<PersonAdd />}
                onClick={() => setDialogOpen(true)}
                sx={{ textTransform: 'none', color: '#00a09d' }}
              >
                Add new salesperson
              </Button>
            </Box>
          }
        />
      </Box>

      {/* Add Salesperson Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '4px' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          py: 1.5,
          px: 2,
          backgroundColor: '#f8f8f8',
        }}>
          <Typography sx={{ fontWeight: 500, fontSize: '16px', color: '#333' }}>
            Create Salesperson
          </Typography>
          <IconButton 
            size="small" 
            onClick={handleCloseDialog}
            sx={{ color: '#666' }}
          >
            <Close sx={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
            <Box sx={{ 
              width: 80, 
              height: 80, 
              backgroundColor: '#f5f0f0', 
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Person sx={{ fontSize: 40, color: '#00a09d' }} />
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                variant="standard"
                value={newSalesperson.name}
                onChange={(e) => setNewSalesperson({ ...newSalesperson, name: e.target.value })}
                placeholder="Full Name"
                InputProps={{
                  disableUnderline: true,
                  sx: { 
                    fontSize: '24px', 
                    fontWeight: 400,
                    color: '#333',
                    borderBottom: '1px solid #e0e0e0',
                    pb: 0.5,
                  }
                }}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Email sx={{ fontSize: '16px', color: '#666' }} />
                <TextField
                  fullWidth
                  variant="standard"
                  value={newSalesperson.email}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, email: e.target.value })}
                  placeholder="Email"
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: '14px', color: '#666', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                  }}
                />
              </Box>
            </Box>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone sx={{ fontSize: '16px', color: '#666' }} />
                <TextField
                  fullWidth
                  variant="standard"
                  value={newSalesperson.phone}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, phone: e.target.value })}
                  placeholder="Phone"
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business sx={{ fontSize: '16px', color: '#666' }} />
                <TextField
                  fullWidth
                  variant="standard"
                  value={newSalesperson.department}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, department: e.target.value })}
                  placeholder="Department"
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', mb: 1 }}>
                <Typography sx={{ width: '80px', fontSize: '13px', color: '#333', pt: 1 }}>Role</Typography>
                <TextField
                  fullWidth
                  select
                  variant="standard"
                  value={newSalesperson.role}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, role: e.target.value })}
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                  }}
                >
                  <MenuItem value="sales">Sales</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </TextField>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ 
          px: 2, 
          py: 1.5, 
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f8f8',
          justifyContent: 'flex-start',
        }}>
          <Button
            variant="contained"
            onClick={handleAddSalesperson}
            sx={{
              backgroundColor: '#00a09d',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#008f8c' },
            }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            sx={{
              backgroundColor: '#4a4a4a',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#333' },
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SalespersonSelect;
